import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { AlertTriangle, Trash2, ShieldAlert, Clock, Ban, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DeleteAccountSection = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [step, setStep] = useState(1); // 1: warning, 2: re-auth, 3: confirm
  const [formData, setFormData] = useState({
    indos_number: '',
    password: '',
    confirm_text: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Don't show for admins
  if (user?.role === 'admin' || user?.role === 'sub_admin') {
    return null;
  }

  const handleOpenDialog = () => {
    setDialogOpen(true);
    setStep(1);
    setFormData({ indos_number: '', password: '', confirm_text: '' });
    setError('');
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setStep(1);
    setFormData({ indos_number: '', password: '', confirm_text: '' });
    setError('');
  };

  const handleNextStep = () => {
    if (step === 1) {
      setStep(2);
    } else if (step === 2) {
      // Validate re-auth fields
      if (!formData.indos_number || !formData.password) {
        setError('Please enter both INDoS number and password');
        return;
      }
      if (formData.indos_number.toUpperCase() !== user?.indos_number?.toUpperCase()) {
        setError('INDoS number does not match your account');
        return;
      }
      setError('');
      setStep(3);
    }
  };

  const handleDeleteAccount = async () => {
    if (formData.confirm_text !== 'DELETE') {
      setError('Please type DELETE to confirm');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/auth/delete-account`, {
        indos_number: formData.indos_number,
        password: formData.password,
        confirm_text: formData.confirm_text
      });

      toast.success('Your account has been deleted. You have 30 days to recover it.');
      handleCloseDialog();
      
      // Logout and redirect
      logout();
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card className="border-red-200 bg-red-50/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-700">
            <Trash2 className="w-5 h-5" />
            Delete Account
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600 mb-4">
            Permanently delete your account and all associated data. This action can be undone within 30 days.
          </p>
          <Button
            variant="outline"
            className="text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700"
            onClick={handleOpenDialog}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete My Account
          </Button>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              {step === 1 && 'Delete Your Account'}
              {step === 2 && 'Verify Your Identity'}
              {step === 3 && 'Final Confirmation'}
            </DialogTitle>
            <DialogDescription>
              {step === 1 && 'Please read the following information carefully before proceeding.'}
              {step === 2 && 'For security, please re-enter your credentials.'}
              {step === 3 && 'Type DELETE to confirm account deletion.'}
            </DialogDescription>
          </DialogHeader>

          {/* Step 1: Warning */}
          {step === 1 && (
            <div className="space-y-4 py-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                  <ShieldAlert className="w-5 h-5" />
                  What happens when you delete your account:
                </h4>
                <ul className="space-y-2 text-sm text-amber-800">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Your account will be <strong>deactivated immediately</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>You have <strong>30 days to recover</strong> your account by contacting support</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Trash2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>After 30 days, all your data will be <strong>permanently deleted</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Ban className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>You <strong>will not be able to create a new account</strong> with this INDoS for 30 days</span>
                  </li>
                </ul>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>💡 Tip:</strong> If you just want to take a break, you can simply log out. 
                  Your data will be safe and you can come back anytime.
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Re-authentication */}
          {step === 2 && (
            <div className="space-y-4 py-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-700 mb-4">
                  For your security, please verify your identity by entering your credentials.
                </p>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="indos">INDoS Number</Label>
                    <Input
                      id="indos"
                      type="text"
                      value={formData.indos_number}
                      onChange={(e) => setFormData({ ...formData, indos_number: e.target.value.toUpperCase() })}
                      placeholder="Enter your INDoS number"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Enter your password"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Final Confirmation */}
          {step === 3 && (
            <div className="space-y-4 py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800 mb-4">
                  <strong>⚠️ This is your last chance to cancel.</strong>
                </p>
                <p className="text-sm text-red-700">
                  To confirm deletion, type <strong className="font-mono bg-red-100 px-2 py-1 rounded">DELETE</strong> in the box below:
                </p>
              </div>

              <div>
                <Input
                  type="text"
                  value={formData.confirm_text}
                  onChange={(e) => setFormData({ ...formData, confirm_text: e.target.value.toUpperCase() })}
                  placeholder="Type DELETE to confirm"
                  className="text-center font-mono text-lg tracking-wider"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex gap-2">
            {step > 1 && (
              <Button
                variant="outline"
                onClick={() => setStep(step - 1)}
                disabled={loading}
              >
                Back
              </Button>
            )}
            <Button
              variant="outline"
              onClick={handleCloseDialog}
              disabled={loading}
            >
              Cancel
            </Button>
            {step < 3 ? (
              <Button
                variant="destructive"
                onClick={handleNextStep}
              >
                Continue
              </Button>
            ) : (
              <Button
                variant="destructive"
                onClick={handleDeleteAccount}
                disabled={loading || formData.confirm_text !== 'DELETE'}
              >
                {loading ? 'Deleting...' : 'Delete My Account'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DeleteAccountSection;
